import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Monogram } from "./Team";

export default function TeamMember() {
    const { slug } = useParams();
    const [member, setMember] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        api.get(`/team/${slug}`)
            .then((r) => setMember(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="px-6 md:px-12 lg:px-20 py-24">
                <h1 className="font-serif text-4xl mb-6">Team member not found</h1>
                <Link to="/team" className="eyebrow text-[#B8860B]">← Back to the team</Link>
            </div>
        );
    }
    if (!member) {
        return <div className="px-6 md:px-12 lg:px-20 py-24 font-serif text-2xl text-black/50">Loading…</div>;
    }

    const paragraphs = (member.bio || "").split("\n").map((p) => p.trim()).filter(Boolean);

    return (
        <div data-testid={`team-member-page-${member.slug}`}>
            <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-16 md:pb-24">
                <Link
                    to="/team"
                    data-testid="back-to-team"
                    className="inline-flex items-center gap-2 eyebrow text-black/50 hover:text-[#B8860B] mb-10 text-[11px]"
                >
                    ← Meet the Team
                </Link>
                <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
                    <div className="md:col-span-5">
                        {member.photo_url ? (
                            <img
                                src={member.photo_url}
                                alt={member.name}
                                className="w-full h-[440px] md:h-[560px] object-cover object-top"
                            />
                        ) : (
                            <Monogram name={member.name} className="w-full h-[440px] md:h-[560px]" />
                        )}
                    </div>
                    <div className="md:col-span-7">
                        <div className="eyebrow text-[#B8860B] mb-6">{member.role}</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-10">
                            {member.name}
                        </h1>
                        {member.quote && (
                            <blockquote className="border-l-2 border-[#D4AF37] pl-6 font-serif italic text-2xl md:text-3xl leading-snug text-black/70 mb-12">
                                "{member.quote}"
                            </blockquote>
                        )}
                        <div className="space-y-6 text-lg text-black/70 font-light leading-relaxed max-w-2xl">
                            {paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-20 py-20 md:py-28">
                <div className="grid md:grid-cols-12 items-center gap-10 max-w-6xl">
                    <div className="md:col-span-8">
                        <div className="eyebrow text-[#D4AF37] mb-6">The Gift of Kindness</div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                            Meet the rest of the team behind the movement.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/team"
                            data-testid="member-back-cta"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 hover:bg-white transition-colors eyebrow"
                        >
                            Full Team <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
